import _isEmpty from 'lodash/isEmpty';
import {
  ITransformedReadProductsResponse,
  IEntitlementInstances,
  IOrgEntitlementInstances,
  IMspTransformedProductData,
  IEntitlementInstanceProduct,
  IEntitlementInstance,
  ICompositeProductInstances,
  IProductInstance,
  IAdditionalMetaData,
} from 'src/app/interfaces/ICustomersLists';
import {
  CISCO_SSE_AUS_ADDON,
  CISCO_SSE_PRODUCT,
  secure_access_SKUs,
  CISCO_SDWAN_SERVICE_DESCRIPTION,
  TEST_CISCO_SASE_SERVICE,
  CISCO_SASE_SERVICE_DESCRIPTION,
  TEST_CISCO_SDWAN_SERVICE_DESCRIPTION,
} from './constant';
import { ProvisioningStatus } from '../enums/ProvisioningStatus';
import {
  IProductTransformed,
  IProductTransformedData,
} from '../interfaces/IProductList';

export const mapSkuToDescription = (
  entitlement: IEntitlementInstances | IOrgEntitlementInstances
): IEntitlementInstances | IOrgEntitlementInstances => {
  if (entitlement?.entitlement?.description === '') {
    const newDescription = secure_access_SKUs.get(
      entitlement?.entitlement?.value
    );
    if (newDescription) {
      entitlement.entitlement.description = newDescription;
    }
  }
  return entitlement;
};

export const generateProductName = (
  product: ITransformedReadProductsResponse,
  entitlement: IEntitlementInstances | IOrgEntitlementInstances
): string => {
  const mappedEntitlement = mapSkuToDescription(entitlement);

  if (mappedEntitlement?.entitlement?.description) {
    return product?.isOrgEntitled
      ? mappedEntitlement.entitlement.description
      : `${product?.productName} ${mappedEntitlement.entitlement.description}`;
  }
  return product?.productName;
};

export const transformMspProductData = (
  products: ITransformedReadProductsResponse[]
): IMspTransformedProductData[] => {
  if (!Array.isArray(products)) {
    return [];
  }

  return products.flatMap((product) => {
    if (product?.isOrgEntitled) {
      return {
        ProductName: product.productDisplayName,
        PurchasedEntitlements: product.quantity ?? 0,
        subscriptionId: product.subscriptionId,
        endCustomerName: product.endCustomerName ?? [],
      };
    } else {
      return (
        product?.entitlementInstances?.map(
          (entitlement: IEntitlementInstances) => ({
            ProductName: generateProductName(product, entitlement),
            PurchasedEntitlements: entitlement.quantity,
            subscriptionId: entitlement.subscription.externalId,
            endCustomerName: entitlement.subscription.endCustomerName ?? [],
          })
        ) || []
      );
    }
  });
};

export const hasIncompatibleAusAddon = (
  products: IEntitlementInstanceProduct[]
): boolean => {
  return products.some(
    (product) =>
      product?.productName === CISCO_SSE_PRODUCT &&
      product?.entitlementInstances?.some(
        (instance) =>
          instance?.entitlement?.value === CISCO_SSE_AUS_ADDON &&
          instance?.isAddOnRegionCompatible === false
      )
  );
};

const findProduct = (
  data: IProductTransformedData,
  serviceName: string
): IProductTransformed | undefined => {
  return data.allProducts.find(
    (product) => product.rawProductName === serviceName
  );
};

export const getSaseProduct = (
  productData: IProductTransformedData
): IProductTransformed | undefined => {
  return findProduct(productData, CISCO_SASE_SERVICE_DESCRIPTION);
};

/**
 * Return true if role admin is converted to net admins
 * @returns
 * @param additionalData
 */
export function hasRoleNetAdminTransfer(
  additionalData: IAdditionalMetaData[]
): boolean {
  const admin = additionalData.find(
    (additionalDataKeyValue) =>
      additionalDataKeyValue.key === 'administrator_access'
  );
  return admin?.value === 'Yes';
}

export const getSDWANTenantProduct = (
  productData: IProductTransformedData
): IProductTransformed | undefined => {
  return findProduct(productData, CISCO_SDWAN_SERVICE_DESCRIPTION);
};

export const getTestSaseProduct = (
  productData: IProductTransformedData
): IProductTransformed | undefined => {
  return findProduct(productData, TEST_CISCO_SASE_SERVICE);
};

export const getTestSDWANTenantProduct = (
  productData: IProductTransformedData
): IProductTransformed | undefined => {
  return findProduct(productData, TEST_CISCO_SDWAN_SERVICE_DESCRIPTION);
};

export const isSDWANTenantNotFound = (
  saseProduct?: IProductTransformed,
  sdwanTenantProduct?: IProductTransformed
): boolean => {
  return (
    !!saseProduct &&
    [
      ProvisioningStatus.ACTION_REQUIRED,
      ProvisioningStatus.IN_PROGRESS,
    ].includes(saseProduct.provisionStatus as ProvisioningStatus) &&
    !!sdwanTenantProduct &&
    sdwanTenantProduct.provisionStatus === ProvisioningStatus.IN_PROGRESS
  );
};

export const isSDWANTenantFound = (
  saseProduct?: IProductTransformed,
  sdwanTenantProduct?: IProductTransformed
): boolean => {
  return (
    !!saseProduct &&
    [
      ProvisioningStatus.IN_PROGRESS,
      ProvisioningStatus.ACTION_REQUIRED,
    ].includes(saseProduct.provisionStatus as ProvisioningStatus) &&
    !!sdwanTenantProduct &&
    sdwanTenantProduct.provisionStatus === ProvisioningStatus.FINISHED
  );
};

export const isSASEReady = (
  saseProduct?: IProductTransformed,
  sdwanTenantProduct?: IProductTransformed
): boolean => {
  return (
    !!saseProduct &&
    saseProduct.provisionStatus === ProvisioningStatus.FINISHED &&
    !!sdwanTenantProduct &&
    sdwanTenantProduct.provisionStatus === ProvisioningStatus.FINISHED
  );
};
export const isSASEFailed = (
  saseProduct?: IProductTransformed | null,
  sdwanTenantProduct?: IProductTransformed | null
): boolean => {
  // if not present , then no point in failure
  const ifNotPresent = !!saseProduct && !!sdwanTenantProduct;
  return (
    ifNotPresent &&
    saseProduct.provisionStatus === ProvisioningStatus.ERROR &&
    sdwanTenantProduct.provisionStatus === ProvisioningStatus.FINISHED
  );
};

export const findSseProduct = (
  products: IEntitlementInstanceProduct[],
  productNameToFind: string
): IEntitlementInstanceProduct | undefined => {
  return products.find((product) => product.productName === productNameToFind);
};

export const getSseProduct = (
  products: IEntitlementInstanceProduct[]
): IEntitlementInstanceProduct | undefined => {
  return findSseProduct(products, CISCO_SSE_PRODUCT);
};

export const isAusAddonInstanceWithRegions = (
  instance: IEntitlementInstance | undefined
): boolean => {
  if (!instance) {
    return false;
  }
  const isAusAddon = instance.entitlement?.value === CISCO_SSE_AUS_ADDON;
  const hasValidRegions = (instance.addOnRegions?.length ?? 0) > 0;
  return isAusAddon && hasValidRegions;
};

export const findAusAddonInstance = (
  sseProduct: IEntitlementInstanceProduct
): IEntitlementInstance | undefined => {
  return sseProduct.entitlementInstances?.find(isAusAddonInstanceWithRegions);
};

export const getFormattedRegionFromAddon = (
  ausAddonInstance: IEntitlementInstance
): string => {
  const firstRegion = ausAddonInstance.addOnRegions?.[0];
  if (firstRegion) {
    return firstRegion.description || firstRegion.name || '';
  }
  return '';
};

export const getRegionForAusAddon = (
  products: IEntitlementInstanceProduct[]
): string => {
  const sseProduct = getSseProduct(products);
  if (!sseProduct) {
    return '';
  }
  const ausAddonInstance = findAusAddonInstance(sseProduct);
  if (!ausAddonInstance) {
    return '';
  }
  return getFormattedRegionFromAddon(ausAddonInstance);
};

export const isAllProductsTrueCompositeList = (
  composites: ICompositeProductInstances[]
): boolean => {
  if (_isEmpty(composites)) {
    return true;
  }
  const saseProduct = composites.find(
    (product) =>
      product.product_name === CISCO_SASE_SERVICE_DESCRIPTION ||
      product.product_name === TEST_CISCO_SASE_SERVICE
  );

  if (
    saseProduct !== undefined &&
    saseProduct.provisioning_status === 'FINISHED'
  ) {
    const allActive = saseProduct.subProductInstances.every(
      (subProductInstance) =>
        subProductInstance.isActive &&
        subProductInstance?.provisioning_status === 'FINISHED'
    );
    return allActive;
  }
  return false;
};

export const extractProduct = (
  products: IProductTransformed[],
  productNameToFind: string
): IProductTransformed | undefined => {
  return products.find(
    (product) => product.rawProductName === productNameToFind
  );
};

export const extractAusAddonInstance = (
  sseProduct: IProductTransformed
): IEntitlementInstance | undefined => {
  return sseProduct.subRows?.find(isAusAddonInstanceWithRegions);
};

export const extractRegionForAusAddon = (
  products: IProductTransformed[]
): string => {
  const sseProduct = extractProduct(products, CISCO_SSE_PRODUCT);
  if (!sseProduct) {
    return '';
  }
  const ausAddonInstance = extractAusAddonInstance(sseProduct);
  if (!ausAddonInstance) {
    return '';
  }
  return getFormattedRegionFromAddon(ausAddonInstance);
};
export function hasSseAusEntitlement(product: IProductInstance): boolean {
  if ((product.entitlement_instances ?? []).length === 0) {
    return false;
  }
  return product.entitlement_instances.some(
    (instance) =>
      instance.entitlement?.value === CISCO_SSE_AUS_ADDON &&
      !instance.isAddOnRegionCompatible
  );
}

/**
 * Compares provisionStatus of products between two data sets.
 * Returns false if any status has changed or if product counts differ.
 */
export function areProductStatusesIdentical(
  oldData: IProductTransformedData,
  newData: IProductTransformedData
): boolean {
  const oldProducts = oldData.allProducts;
  const newProducts = newData.allProducts;

  // 1. Check the hasActiveMPE flag first (Fail Fast)
  if (oldData?.hasActiveMPE !== newData?.hasActiveMPE) {
    return false;
  }

  // 2. Quick check: If the number of products changed, they aren't identical
  if (oldProducts.length !== newProducts.length) {
    return false;
  }

  // 3. Create a Map of the old statuses for fast lookup
  const oldStatusMap = new Map(
    oldProducts.map((item) => [item.productId, item.provisionStatus])
  );

  // 4. Check every product in the new data against the old Map
  for (const product of newProducts) {
    const oldStatus = oldStatusMap.get(product.productId);
    // If the ID doesn't exist in the old data, or the status is different
    if (oldStatus === undefined || oldStatus !== product.provisionStatus) {
      return false;
    }
  }
  // 5. If we reached here, everything matches
  return true;
}
